/**
 * Automatic Change Tracking for ArchiMate Diagrams
 *
 * Automatically detects and logs changes to diagrams including:
 * - Elements added, removed, or modified
 * - Relationships added, removed, or modified
 * - Metadata changes (security, debt, ownership, etc.)
 */

import { ExtendedArchiMateDiagram, ChangelogEntry, Change, ChangeType } from './extended-types';
import { ArchiMateElement } from './types';
import { createChangelogEntry } from './changelog-utils';
import { uuidv7 } from 'uuidv7';

export interface ChangeTrackingOptions {
  userName?: string;
  autoTrack?: boolean;
}

/**
 * Deep equality comparison for objects
 * More reliable than JSON.stringify as it handles property order and special values
 */
function createChange(
  type: ChangeType,
  diagramId: string,
  diagramName: string,
  elementId: string | undefined,
  message: string,
  user?: string,
  before?: any,
  after?: any,
  relationshipId?: string
): Change {
  return {
    id: uuidv7(),
    type,
    timestamp: Date.now(),
    user,
    diagramId,
    diagramName,
    message,
    elementId,
    relationshipId,
    before,
    after,
    impactedTeams: [],
  };
}

/**
 * Detects changes between two diagram states and returns changelog entry
 */
export function detectChanges(
  oldDiagram: ExtendedArchiMateDiagram,
  newDiagram: ExtendedArchiMateDiagram,
  options: ChangeTrackingOptions = {}
): ChangelogEntry | null {
  const { userName = 'System' } = options;
  const changes: Change[] = [];
  const timestamp = Date.now();

  // Detect element changes
  const elementChanges = detectElementChanges(oldDiagram, newDiagram, userName, timestamp);
  changes.push(...elementChanges);

  // Detect relationship changes
  const relationshipChanges = detectRelationshipChanges(oldDiagram, newDiagram, userName, timestamp);
  changes.push(...relationshipChanges);

  // Detect metadata changes
  const metadataChanges = detectMetadataChanges(oldDiagram, newDiagram, userName, timestamp);
  changes.push(...metadataChanges);

  // If no changes detected, return null
  if (changes.length === 0) {
    return null;
  }

  // Create a single changelog entry for this update
  const today = new Date().toISOString().split('T')[0];
  const entry = createChangelogEntry(today, changes, userName);

  return entry;
}

/**
 * Detect element additions, removals, and modifications
 */
function detectElementChanges(
  oldDiagram: ExtendedArchiMateDiagram,
  newDiagram: ExtendedArchiMateDiagram,
  user: string,
  timestamp: number
): Change[] {
  const changes: Change[] = [];

  // Create maps for quick lookup
  const oldElementsMap = new Map(oldDiagram.elements.map(el => [el.id, el]));
  const newElementsMap = new Map(newDiagram.elements.map(el => [el.id, el]));

  // Detect additions
  for (const [id, element] of newElementsMap) {
    if (!oldElementsMap.has(id)) {
      changes.push({
        id: uuidv7(),
        type: 'element-added',
        timestamp,
        user,
        diagramId: newDiagram.id,
        diagramName: newDiagram.name,
        elementId: id,
        elementName: element.name,
        message: `Added ${element.type}: ${element.name}`,
        after: element,
        impactedTeams: [],
      });
    }
  }

  // Detect removals
  for (const [id, element] of oldElementsMap) {
    if (!newElementsMap.has(id)) {
      changes.push({
        id: uuidv7(),
        type: 'element-removed',
        timestamp,
        user,
        diagramId: newDiagram.id,
        diagramName: newDiagram.name,
        elementId: id,
        elementName: element.name,
        message: `Removed ${element.type}: ${element.name}`,
        before: element,
        impactedTeams: [],
      });
    }
  }

  // Detect modifications
  for (const [id, newElement] of newElementsMap) {
    const oldElement = oldElementsMap.get(id);
    if (oldElement && hasElementChanged(oldElement, newElement)) {
      changes.push({
        id: uuidv7(),
        type: 'element-modified',
        timestamp,
        user,
        diagramId: newDiagram.id,
        diagramName: newDiagram.name,
        elementId: id,
        elementName: newElement.name,
        message: `Modified ${newElement.type}: ${newElement.name}`,
        before: serializeElement(oldElement),
        after: serializeElement(newElement),
        impactedTeams: [],
      });
    }
  }

  return changes;
}

/**
 * Detect relationship additions and removals
 */
function detectRelationshipChanges(
  oldDiagram: ExtendedArchiMateDiagram,
  newDiagram: ExtendedArchiMateDiagram,
  user: string,
  timestamp: number
): Change[] {
  const changes: Change[] = [];

  const oldRelsMap = new Map(oldDiagram.relationships.map(rel => [rel.id, rel]));
  const newRelsMap = new Map(newDiagram.relationships.map(rel => [rel.id, rel]));

  // Detect additions
  for (const [id, rel] of newRelsMap) {
    if (!oldRelsMap.has(id)) {
      const source = newDiagram.elements.find(e => e.id === rel.sourceId);
      const target = newDiagram.elements.find(e => e.id === rel.targetId);
      changes.push({
        id: uuidv7(),
        type: 'relationship-added',
        timestamp,
        user,
        diagramId: newDiagram.id,
        diagramName: newDiagram.name,
        relationshipId: id,
        message: `Added ${rel.type} relationship: ${source?.name || 'Unknown'} → ${target?.name || 'Unknown'}`,
        after: rel,
        impactedTeams: [],
      });
    }
  }

  // Detect removals
  for (const [id, rel] of oldRelsMap) {
    if (!newRelsMap.has(id)) {
      const source = oldDiagram.elements.find(e => e.id === rel.sourceId);
      const target = oldDiagram.elements.find(e => e.id === rel.targetId);
      changes.push({
        id: uuidv7(),
        type: 'relationship-removed',
        timestamp,
        user,
        diagramId: newDiagram.id,
        diagramName: newDiagram.name,
        relationshipId: id,
        message: `Removed ${rel.type} relationship: ${source?.name || 'Unknown'} → ${target?.name || 'Unknown'}`,
        before: rel,
        impactedTeams: [],
      });
    }
  }

  return changes;
}

/**
 * Detect metadata changes (security, debt, ownership, etc.)
 */
function detectMetadataChanges(
  oldDiagram: ExtendedArchiMateDiagram,
  newDiagram: ExtendedArchiMateDiagram,
  user: string,
  timestamp: number
): Change[] {
  const changes: Change[] = [];

  // Create element maps for efficient lookup
  const newElementsMap = new Map(newDiagram.elements.map(el => [el.id, el]));
  const oldElementsMap = new Map(oldDiagram.elements.map(el => [el.id, el]));

  // Check metadata changes for existing elements
  for (const element of newDiagram.elements) {
    const oldMetadata = oldDiagram.elementMetadata[element.id];
    const newMetadata = newDiagram.elementMetadata[element.id];

    if (JSON.stringify(oldMetadata) !== JSON.stringify(newMetadata)) {
      // Determine what kind of metadata changed
      const metadataTypes: string[] = [];

      if (JSON.stringify(oldMetadata?.security) !== JSON.stringify(newMetadata?.security)) {
        metadataTypes.push('security');
      }
      if (JSON.stringify(oldMetadata?.debt) !== JSON.stringify(newMetadata?.debt)) {
        metadataTypes.push('technical debt');
      }
      if (JSON.stringify(oldMetadata?.ownership) !== JSON.stringify(newMetadata?.ownership)) {
        metadataTypes.push('ownership');
      }
      if (JSON.stringify(oldMetadata?.cost) !== JSON.stringify(newMetadata?.cost)) {
        metadataTypes.push('cost');
      }
      if (JSON.stringify(oldMetadata?.performance) !== JSON.stringify(newMetadata?.performance)) {
        metadataTypes.push('performance');
      }

      if (metadataTypes.length > 0) {
        changes.push({
          id: uuidv7(),
          type: 'element-modified',
          timestamp,
          user,
          diagramId: newDiagram.id,
          diagramName: newDiagram.name,
          elementId: element.id,
          elementName: element.name,
          message: `Updated ${metadataTypes.join(', ')} for ${element.name}`,
          before: oldMetadata,
          after: newMetadata,
          impactedTeams: [],
        });
      }
    }
  }

  // Check metadata for removed elements (elements in old but not in new)
  for (const oldElement of oldDiagram.elements) {
    if (!newElementsMap.has(oldElement.id)) {
      const oldMetadata = oldDiagram.elementMetadata[oldElement.id];
      // If the removed element had metadata, it's already tracked by element removal
      // but we should note if significant metadata existed
      if (oldMetadata && Object.keys(oldMetadata).length > 0) {
        // This is informational - the element removal already captures this
        // but we could add a specific metadata removal change if needed
        // For now, we rely on the element removal change having the metadata in 'before'
      }
    }
  }

  return changes;
}

/**
 * Check if an element has meaningful changes
 */
function hasElementChanged(oldElement: ArchiMateElement, newElement: ArchiMateElement): boolean {
  // Ignore position and size changes for change tracking
  // Only track meaningful changes like name, type, layer, documentation
  return (
    oldElement.name !== newElement.name ||
    oldElement.type !== newElement.type ||
    oldElement.layer !== newElement.layer ||
    oldElement.documentation !== newElement.documentation
  );
}

/**
 * Serialize element to a simple object for changelog
 */
function serializeElement(element: ArchiMateElement): any {
  return {
    name: element.name,
    type: element.type,
    layer: element.layer,
    documentation: element.documentation,
  };
}

/**
 * Apply change tracking to a diagram update
 * This is the main function to use in components
 */
export function trackDiagramChange(
  oldDiagram: ExtendedArchiMateDiagram,
  newDiagram: ExtendedArchiMateDiagram,
  options: ChangeTrackingOptions = {}
): ExtendedArchiMateDiagram {
  const { autoTrack = true } = options;

  if (!autoTrack) {
    return newDiagram;
  }

  // Detect changes
  const changelogEntry = detectChanges(oldDiagram, newDiagram, options);

  // If no changes detected, return as-is
  if (!changelogEntry) {
    return newDiagram;
  }

  // Add changelog entry to the diagram
  return {
    ...newDiagram,
    changelog: [...newDiagram.changelog, changelogEntry],
  };
}
